<template>
  <div>
    <RejectCommentLayout :comment-text="reject_comment" />
    <v-card>
      <v-alert
          color="purple darken-2"
          type="warning"
          icon="mdi-hand-back-left"
          prominent
          text
          class="rounded-bl-0 rounded-br-0"
        >
          <h5>{{ $t('forms.info.newExternal.title') }}</h5>
          <div v-html="$t('forms.info.newExternal.message')"></div>
          <v-divider
            class="my-4 info"
            style="opacity: 0.22"
          ></v-divider>
          <v-row>
            <v-col
              class="text-center"
              justify="space-around"
            >
              <v-btn
                color="purple darken-4"
                outlined
                :to="`/my-forms/researcher/newResearcher`"
              >
                {{ $t('forms.info.newExternal.button') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-alert>

      <v-form ref="form" class="pa-6">
        <h5>
          {{ $t("form.givenAndFamilyName") }}
        </h5>
        <v-row>
          <v-col cols="12" md="6" class="pb-0">
            <v-text-field
              v-model="investigator.person.preferred_name.given_name"
              @blur="normalizePersonNameField(investigator.person.preferred_name, 'given_name')"
              :label="$t('form.name')"
              outlined
              :readonly="disabledForm"
              :rules="rules.nameRules"
              :counter="40"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6" class="pb-0">
            <v-text-field
              v-model="investigator.person.preferred_name.family_name"
              @blur="normalizePersonNameField(investigator.person.preferred_name, 'family_name')"
              :label="$t('form.familyName')"
              outlined
              :readonly="disabledForm"
              :rules="rules.nameRules"
              :counter="40"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-col cols="12" class="py-0" v-if="similarities && similarities.length > 0">
          <v-alert
            class="mb-8 d-block mt-3"
            style="max-width: 100%; overflow: hidden; text-overflow: ellipsis;"
            color="yellow darken-4"
            icon="mdi-alert-box"
            type="warning"
            text
          >
            {{  $t('form.researcher.similarities') }}
            <v-list light style="margin-left:-0.5em;">
              <v-list-item v-for="similarity in similarities" :key="similarity.id">
                <v-list-item-content>
                  <v-list-item-title dark>
                    <v-row>
                      <v-col cols="10" md="11">
                        <a :href="'https://citius.gal/team/' + similarity.slug" target="_blank" style="white-space: break-spaces;">{{ similarity.preferred_name.given_name + " " + similarity.preferred_name.family_name }}</a>
                      </v-col>
                      <v-col cols="2" md="1">
                        <v-btn
                          color="primary"
                          :to = "'/researchers/' + similarity.id"
                          icon
                          text
                        >
                          <v-icon>mdi-pencil</v-icon>
                        </v-btn>
                      </v-col> 
                    </v-row>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-alert>
        </v-col>
        <h5>{{ $t("form.email") }}</h5>
        <ArrayFieldForm
          v-model="investigator.person.email"
          :read-only="disabledForm"
          name-field="email"
          :label="$t('form.email')"
        />
        <h5>{{ $t("form.incharge") }}</h5>
        <SelectPeopleList
          :disabled-form="disabledForm"
          :items-selected="investigator.person.incharge"
        />

        <v-row>
          <v-col cols="12" md="6" class="pb-0">
            <h5>
              {{ $t("form.dni") }}
            </h5>
            <v-row>
              <v-col cols="4" class="pb-0">
                <v-select
                  v-model="investigator.person.id_number.type"
                  :label="$t('type')"
                  outlined
                  :items="identificationOptions"
                  item-text="text"
                  item-value="value"
                  :readonly="disabledForm"
                  :rules="rules.fieldRequired"
                ></v-select
              ></v-col>
              <v-col cols="8" class="pb-0">
                <v-text-field
                  v-model="investigator.person.id_number.value"
                  :label="$t('label.value')"
                  outlined
                  :readonly="disabledForm"
                  :rules="[dniValueRulesFunction]"
                  ref="idNumberText"
                  @change="$refs.idNumberText.validate()"
                >
                </v-text-field>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" md="3" class="pb-0">
            <h5>
              {{ $t("form.mobile") }}
            </h5>
            <v-row>
              <v-col class="pb-0">
                <v-text-field
                  v-model="investigator.person.number"
                  outlined
                  :label="$t('form.mobile')"
                  :readonly="disabledForm"
                  :rules="[telephoneRuleFunction]"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" md="3" class="pb-0">
            <h5>{{ $t("form.gender") }}</h5>
            <v-row>
              <v-col class="pb-0 mt-1">
                <v-btn-toggle
                  v-model="investigator.person.gender"
                  mandatory
                  :disabled="disabledForm"
                >
                  <v-btn value="male" :class="{'disable-events': disabledForm}">
                    {{ $t("form.male") }}
                  </v-btn>
                  <v-btn value="female" :class="{'disable-events': disabledForm}">
                    {{ $t("form.female") }}
                  </v-btn>
                </v-btn-toggle>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        
        <h5>
          {{ $t("form.motivationAccount") }}
        </h5>
        <v-text-field
          v-model="investigator.motivation"
          outlined
          :label="$t('form.motivationAccount')"
          :readonly="disabledForm"
          :hint="$t('forms.hint.motivationAccount')"
          persistent-hint
        ></v-text-field>

        <h5>
          {{ $t("form.list.additional.services") }}
        </h5>
        <div class="text-body-2 mb-5">{{ $t("form.additional.services_explanation") }}</div>
        <v-row class="mt-5">
          <v-col
            cols="12"
            sm="6"
            md="4"
            lg="3"
            v-for="service in sortedServices"
            v-show="isServiceAvailableInForms(service)"
            :key="service.code"
            class="py-0"
          >
            <v-checkbox
              multiple
              v-model="investigator.account.services"
              :value="service.id"
              :readonly="disabledForm || !isServiceAvailableInForms(service)"
              class="service-checkbox py-0 my-0"
              dense
            >
              <template v-slot:label>
                <div>
                  <div class="service-label-row">
                    <span class="service-label-name">{{ service.name }}</span>
                    <v-btn
                      icon
                      small
                      color="primary"
                      class="service-info-button"
                      @click.stop="openServiceInfo(service)"
                    >
                      <v-icon small>mdi-information-outline</v-icon>
                    </v-btn>
                  </div>
                  <div
                    v-if="!isServiceAvailableInForms(service)"
                    class="service-unavailable-hint"
                  >
                    {{ $t("form.service_unavailable_in_forms_hint") }}
                  </div>
                </div>
              </template>
            </v-checkbox>
          </v-col>
        </v-row>

        <v-divider />

        <UserFormActions
          form_name_text="External people"
          :form_name="form_name"
          :object_type="object_type"
          :data="processData"
          :state="state"
          :refForm="$refs.form"
          @change-state="changeState"
          :paginated-form="true"
          class="text-center pt-6"
        />

      </v-form>

      <v-alert
          color="grey darken-2"
          type="warning"
          icon="mdi-information-variant-circle"
          prominent
          text
          class="rounded-tl-0 rounded-tr-0"
        >
          <div v-html="$t('forms.info.newExternal.accountmessage')"></div>
          <v-row>
            <v-col
              class="text-center"
              justify="space-around"
            >
            </v-col>
          </v-row>
        </v-alert>
      <v-dialog v-model="serviceInfoDialog" max-width="600px">
        <v-card v-if="selectedService">
          <v-card-title class="headline">
            {{ selectedService.name }}
          </v-card-title>
          <v-card-text>
            <div class="mb-4">
              <strong>{{ $t("form.description") }}:</strong>
              <div
                v-if="getServiceDescription(selectedService)"
                v-html="getServiceDescription(selectedService)"
              />
              <div v-else>
                {{ $t("table.features.missingDescription") }}
              </div>
            </div>
            <div class="mb-2">
              <strong>{{ $t("form.restricted") }}:</strong>
              <span>
                {{
                  selectedService.restricted
                    ? $t("table.features.restricted")
                    : $t("table.features.notRestricted")
                }}
              </span>
            </div>
            <div class="mb-2">
              <strong>{{ $t("form.requires_agreement") }}:</strong>
              <span>
                {{
                  selectedService.requires_agreement
                    ? $t("table.features.requiresAgreement")
                    : $t("table.features.notRequiresAgreement")
                }}
              </span>
            </div>
            <div>
              <strong>{{ $t("form.documentation_link") }}:</strong>
              <span v-if="selectedService.documentation_link">
                <a
                  :href="selectedService.documentation_link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ selectedService.documentation_link }}
                </a>
              </span>
              <span v-else>-</span>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="closeServiceInfo">
              {{ $t("button.close") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";
import RejectCommentLayout from "../../layouts/RejectCommentLayout";
import ArrayFieldForm from "../../reusables/ArrayFieldForm";
import SelectPeopleList from "../../reusables/SelectPeopleList";
import UserFormActions from "../../action_buttons/UserFormActions";
import changeState from "../../../mixins/changeState";
import validationRules from "../../../mixins/validationRules";
import investigatorData from "../../../mixins/investigator_data";
import investigator_model from "../../../mixins/model_utils/investigator_model";
import serviceDisplay from "../../../mixins/serviceDisplay";
export default {
  mixins: [changeState, validationRules, investigatorData, investigator_model, serviceDisplay],
  components: {
    SelectPeopleList,
    RejectCommentLayout,
    ArrayFieldForm,
    UserFormActions
  },

  props:{
    similarities: {
      type: Array,
      default: () => [],
    },
  },

  data: () => ({
    form_name: "externalPeopleForm",
    step: 1,
    maxBirthdayDate: "",
    tab: null,
    menu: false,
    state: "",
    reject_comment: "",
    object_type: "investigator"
  }),

  created() {
    this.loadBirthdayDate();
    this.getResearchAreas();
    this.getServices();
    if (this.updatePersonal || this.updatePersonalForm) {
      this.getData();
    } else {
      this.loadDefaultPersonInCharge();
    }
  },

  watch:{
    'investigator.person.preferred_name':{
      handler: function(val, oldVal){
        console.log(this.investigator.person.preferred_name);
        if(this.investigator.person.preferred_name.given_name !== "" && this.investigator.person.preferred_name.family_name !== "" ){
          console.log("searching!");
          axios({ url: this.$store.state.endpoint + "/similar/person", method: "GET", params: { search: this.investigator.person.preferred_name.given_name + " " + this.investigator.person.preferred_name.family_name } })
          .then(resp => {
            this.similarities = resp.data;
          });
        }
        else{
          console.log("resetting");
          this.similarities = [];
        }
      },
      deep: true
    },
    services() {
      this.investigator.account.services = this.filterUnavailableFormServices(
        this.investigator.account.services,
        this.services
      );
    }
  },


  computed: {
    createPersonal() {
      return !(this.$route.params.id > -1) && !this.updatePersonal;
    },
    updatePersonalForm() {
      return this.$route.params.id > -1 && !this.updatePersonal;
    },
    updatePersonal() {
      return this.$route.params.id > -1 && this.$route.meta.update;
    },
    disabledForm() {
      let states = ["VD", "PA", "PV", "PC"];
      return states.includes(this.state);
    },
    processData() {
      let number = "";
      if (typeof this.investigator.person.number === "number") {
        number = this.investigator.person.number.toString();
      } else if (typeof this.investigator.person.number === "string") {
        number = this.investigator.person.number.replace(/[^\d]/g, "");
      }
      let emails = [];
      this.investigator.person.email.forEach(email => {
        emails.push(email.trim());
      });
      let data = {
        title:
          this.investigator.person.preferred_name.given_name +
          " " +
          this.investigator.person.preferred_name.family_name,
        object_type: this.object_type,
        form_name: "externalPeopleForm",
        object_id: this.investigator.id,
        data: {
          user: {
            person: {
              preferred_name: {
                given_name:
                  this.investigator.person.preferred_name.given_name !==
                  undefined
                    ? this.investigator.person.preferred_name.given_name.trim()
                    : "",
                family_name:
                  this.investigator.person.preferred_name.family_name !==
                  undefined
                    ? this.investigator.person.preferred_name.family_name.trim()
                    : ""
              },
              email: emails,
              incharge: this.investigator.person.incharge,
              id_number: this.investigator.person.id_number,
              gender: this.investigator.person.gender,
              number: number,
              research_areas: this.investigator.person.research_areas.filter(
                area => Number.isInteger(area)
              )
            },
            account: {
              services: this.investigator.account.services
            },
            motivation: this.investigator.motivation,
          }
        }
      };
      return data;
    }
  },

  methods: {
    loadBirthdayDate() {
      let date = new Date();
      date.setFullYear(date.getFullYear() - 15);
      this.maxBirthdayDate = date.toISOString().substr(0, 10);
    },

    getData() {
      let path =
        this.$store.state.endpoint +
        "/user/moderatedOperations/investigator/" +
        this.$route.params.id;
      axios
        .get(path)
        .then(response => {
          this.investigator = response.data.data.user;
          this.state = response.data.state;
          this.reject_comment = response.data.reject_comment;
          this.investigator.id = response.data.object_id;
        })
        .catch(error => {
          console.log(error);
        });
    }
  }
};
</script>

<style scoped>
.service-label-row {
  display: flex;
  align-items: center;
}

.service-label-name {
  line-height: 1.3;
}

.service-info-button {
  margin-left: 0.15rem;
}

.service-unavailable-hint {
  color: #8d6e63;
  font-size: 0.75rem;
  line-height: 1.3;
}
</style>
